import { useCallback, useEffect, useRef, type ReactNode } from "react";
import type { ChatMessage, ReplyPreview } from "@ferrivia/api-contract";
import { Composer, MessageList, useChatPoll, useChatThread, type ChatThreadTransport, type ComposerHandle, type ThreadDescriptor } from "@ferrivia/chat-ui";
import s from "./ThreadView.module.css";

export interface ThreadViewProps {
  /** One descriptor per available channel. The tab strip only renders when there's more than one — v1 ships a single `human` descriptor, so no tabs. */
  descriptors: ThreadDescriptor[];
  activeDescriptorId: string;
  onActiveDescriptorChange?: (id: string) => void;
  transport: ChatThreadTransport;
  isOwnMessage: (message: ChatMessage) => boolean;
  senderName?: (message: ChatMessage) => string | undefined;
  topNote?: ReactNode;
  /** Whether this thread is the one currently on screen — gates polling and read-marking, since the screen stays mounted even when another tab is active. */
  isActive: boolean;
  onMessagesSeen?: (upToSeq: number) => void;
}

function toReplyPreview(message: ChatMessage): ReplyPreview {
  return {
    id: message.id,
    authorType: message.authorType,
    authorName: message.authorName,
    body: message.body,
    redactedAt: message.redactedAt,
  };
}

export function ThreadView({
  descriptors,
  activeDescriptorId,
  onActiveDescriptorChange,
  transport,
  isOwnMessage,
  senderName,
  topNote,
  isActive,
  onMessagesSeen,
}: ThreadViewProps) {
  const descriptor = descriptors.find((d) => d.id === activeDescriptorId) ?? descriptors[0];
  const { messages, outbox, send, retry, refresh, replyTo, setReplyTo } = useChatThread({ transport });

  // Picking a reply target focuses the composer here rather than leaving it to
  // the composer's own effect: MessageList calls this from inside the swipe's
  // pointerup, and only a focus() made during that gesture gets iOS to raise
  // the keyboard along with it.
  const composerRef = useRef<ComposerHandle>(null);
  const handleReply = useCallback(
    (message: ChatMessage) => {
      setReplyTo(message);
      composerRef.current?.focus();
    },
    [setReplyTo],
  );

  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const { poll } = useChatPoll(async () => {
    if (isActiveRef.current) {
      await refresh();
    }
  });

  useEffect(() => {
    if (isActive) {
      poll();
    }
  }, [isActive, poll]);

  const markedSeqRef = useRef(0);
  useEffect(() => {
    if (!isActive || !onMessagesSeen || messages.length === 0) {
      return;
    }
    const maxSeq = messages.reduce((max, m) => Math.max(max, m.seq), 0);
    if (maxSeq > markedSeqRef.current) {
      markedSeqRef.current = maxSeq;
      onMessagesSeen(maxSeq);
    }
  }, [isActive, messages, onMessagesSeen]);

  if (!descriptor) {
    return null;
  }

  return (
    <div className={s.root}>
      <header className={s.header}>
        <div className={s.avatar}>{descriptor.avatarLabel}</div>
        <div>
          <div className={s.name}>{descriptor.headerName}</div>
          <div className={s.status}>{descriptor.headerStatus}</div>
        </div>
      </header>
      {descriptors.length > 1 ? (
        <div className={s.tabs}>
          {descriptors.map((d) => (
            <button
              key={d.id}
              type="button"
              className={d.id === descriptor.id ? `${s.tab} ${s.tabActive}` : s.tab}
              onClick={() => onActiveDescriptorChange?.(d.id)}
            >
              {d.headerName}
            </button>
          ))}
        </div>
      ) : null}
      <MessageList
        className={s.messages}
        messages={messages}
        outbox={outbox}
        isOwnMessage={isOwnMessage}
        senderName={senderName}
        onRetry={retry}
        onReply={handleReply}
        topNote={topNote}
      />
      <Composer
        ref={composerRef}
        placeholder={descriptor.composerPlaceholder}
        onSend={(text) => send({ v: 1, blocks: [{ type: "text", text }] })}
        replyTo={replyTo ? toReplyPreview(replyTo) : null}
        onClearReply={() => setReplyTo(null)}
      />
    </div>
  );
}
